/**
 * @fileoverview collection of type helpers used across the codebase
 */

/**
 * a kind of naïve implementation of a type-equality generic. There are limitations to
 * this (namely the fact that `any` will short-circuit this function). Great use case is
 * to determine if a type union is equivalent to another one.
 *
 * @note this was inspired by https://github.com/Microsoft/TypeScript/issues/27024#issuecomment-420227672
 */
export type Equals<T, S> = [T] extends [S] ? ([S] extends [T] ? true : false) : false;

export const assertType = <T extends true>() => {};

/**
 * Removes all function properties from an object type.
 *
 * @example
 * type Ex = { a: number, b: string, c: () => void, d: (foo: number) => string };
 * NoCallbacks<Ex> === { a: number, b: string }
 */
export type NoCallbacks<T> = {
  [K in keyof T]: T[K] extends Function ? never : T[K];
};

/**
 * generic type to describe a variadic function with any return signature
 */
type AnyFunction = (...args: any[]) => any;

/**
 * Prepends a string id argument argument to a function type
 *
 * @example
 * type Fn = (foo: number, bar: number) => string;
 * Unbound<Fn> === (id: string, foo: number, bar: number) => string;
 *
 * @note
 * This is useful for annotating callbacks which you expect to pass to instances of a
 * component that gets mapped from a unique id. This is readily reusable with the hook
 * exported from `@services/useBind`
 */
export type Unbound<F extends AnyFunction> = (id: string, ...args: Parameters<F>) => ReturnType<F>;

/**
 * This is used to remove the root cache key from the list of arguments to a fetcher function
 */
export type RemoveFirstArgument<Args extends readonly any[]> = Args extends [any, ...infer Rest]
  ? Rest
  : Args extends readonly [any, ...infer Rest]
    ? Rest
    : never;

/**
 * recursively extract the resolved type of a promise.
 * TODO: when we upgrade to Typescript 4.5+, use the built-in `Awaited` type
 */
export type Awaited<T> = T extends Promise<infer A> ? Awaited<A> : T;

/**
 * parameterized type to extract the non-callback props for a the given type T
 */
export type ExtractValueProps<T> = { [K in keyof T as T[K] extends Function | undefined ? never : K]: T[K] };

/**
 * parameterized type to extract the callback props for the given type T
 */
export type ExtractCallbackProps<T> = { [K in keyof T as T[K] extends Function | undefined ? K : never]: T[K] };

/**
 * parameterized type to extract a type union of actions from a dictionary of callbacks
 *
 * @example
 * ```ts
 * interface MyCallbacks {
 *   foo: (arg: Foo) => void;
 *   bar: (arg: Bar) => void;
 * }
 *
 * type MyActions = ExtractActionTypeUnionFromCallbacks<MyCallbacks>;
 * // `MyActions` will be equivalent to:
 * type MyTypedActions =
 *   | {
 *       type: "foo";
 *       payload: Foo;
 *     }
 *   | {
 *       type: "bar";
 *       payload: Bar;
 *     };
 * ```
 * @note that this will only work for single-arg callbacks
 */
export type ExtractActionTypeUnionFromCallbacks<Callbacks extends { [key in string]: (args: any) => void }> = {
  [T in keyof Callbacks]: {
    type: T;
    payload: Parameters<Callbacks[T]>[0];
  };
}[keyof Callbacks];

/**
 * extracts the fields in the given `Map` type of type `Type`
 *
 * @example
 * ```ts
 * interface MyType {
 *   foo: string;
 *   bar: number;
 * }
 *
 * // this just has { bar: number }
 * type MyNumberOnlyType = ExtractFieldsOfType<MyType, number>;
 * ```
 */
export type ExtractFieldsOfType<Map, Type> = { [P in keyof Map as Map[P] extends Type ? P : never]: Map[P] };

// Gets the length of an array/tuple type. Example:
//
//   type FooLength = LengthOfTuple<[string, number, boolean]>;
//   //=> 3
//
export type LengthOfTuple<T extends any[]> = T extends { length: infer L } ? L : never;

// Gets the type of the last element of a tuple. Example:
//
//   type Foo = LastInTuple<[string, number, boolean]>;
//   //=> boolean
//
//   function lastArg<T extends any[]>(...args: T): LastInTuple<T> {
//     return args[args.length - 1];
//   }
//
//   const bar = lastArg(1);
//   type Bar = typeof bar;
//   //=> number
//
//   const baz = lastArg(1, true, "hey", 123, 1, 2, 3, 4, 5, 6, 7, -1, false);
//   type Baz = typeof baz;
//   //=> boolean
//
export type LastInTuple<T extends any[]> = T[LengthOfTuple<RemoveFirstArgument<T>>];

/**
 * Picks specified keys from an interface and applies partial to the generated output
 *
 *   interface Foo {
 *     bar: string;
 *     baz: boolean;
 *   }
 *
 *   interface PartialFoo extends PartialPick<Foo, "baz"> {}
 *   //=> { baz?: boolean }
 */
export type PartialPick<Type, Key extends keyof Type> = Omit<Type, Key> & Partial<Pick<Type, Key>>;

/**
 * Extracts the singular element from an array
 *
 *   interface Hello {
 *     foo: "bar" | "baz";
 *   }
 *
 *   type MyArray = Hello[];
 *
 *   interface MyElement = ArrayElement<MyArray>;
 *   // => Hello
 */
export type ArrayElement<ArrayType extends readonly unknown[]> = ArrayType extends readonly (infer ElementType)[]
  ? ElementType
  : never;

/**
 * Extracts the keys that has certain type values from the object
 *
 *   type Hello {
 *     foo: string;
 *     foos: string[];
 *   }
 *
 *   type Foos = ExtractTypeKeys<Hello, string[]>; // "foos"
 *
 */
export type ExtractTypeKeys<Obj, Type> = {
  [K in keyof Obj]: Obj[K] extends Type ? K : never;
}[keyof Obj];

/**
 * The next types are from https://hacklewayne.com/typescript-convert-union-to-tuple-array-yes-but-how
 */

/**
 * move a type into the parameters of a function, which makes it a contravariant type
 */
export type Contravariant<T> =
  // T extends any will match any type, but this has the added benefit that a union will distribute, so passing in "a" | "b" returns a type (arg: "a") => void | (arg: "b") => void
  T extends any ? (arg: T) => void : never;

/**
 * move a type to the return of a function, which makes it a covariant type
 */
export type Covariant<T> =
  // T extends any will match any type, but this has the added benefit that a union will distribute, so passing in "a" | "b" returns a type () => "a" | () => "b"
  T extends any ? () => T : never;

/**
 * This tests whether a type is covariant, and if so, returns it
 *
 * [T] is used to ensure that unions like "a" | "b" do not distribute so that "a" | "b" is returned
 */
export type InferCovariant<T> = [T] extends [() => infer I] ? I : never;

/**
 * This tests whether a type is contravariant, and if so, returns it
 *
 * [T] is used to ensure that unions like "a" | "b" do not distribute so that "a" & "b" is returned
 *
 * This means that objects will "unify". Passing in { a: string } | { b: number } returns { a:string, b: number }
 */
export type InferContravariant<T> = [T] extends [(arg: infer I) => void] ? I : never;

/**
 * This monstrosity expands as so:
 * - the inner Contravariant calls distribute unions into unions of functions like (arg: "a") => void | (arg: "b") => void
 * - the outer Contravariant calls does this with the functions to get ((arg: (arg: "a") => void) => void) | ((arg: (arg: "b") => void) => void)
 * Then
 * - the first InferContravariant call converts the "|" into "&" to get (arg: "a") => void & (arg: "b") => void
 * - the second InferContravariant call converts into "a" & "b" but because of contravariance, drops all but the last to return "b"
 *
 * Hence, PickOne
 */
export type PickOne<T> = InferContravariant<InferContravariant<Contravariant<Contravariant<T>>>>;

/**
 * Convert a union like "a" | "b" | "c" to a tuple like ["a", "b", "c"]
 *
 * This is very useful for defining a list of things that then must be usable as an array of those things with type safety if anything is added/removed from the list, or if the order changes
 */
export type ConvertUnionToTuple<T> =
  PickOne<T> extends infer U // assign PickOne<T> to U
    ? Exclude<T, U> extends never // T and U are the same
      ? [T]
      : [...ConvertUnionToTuple<Exclude<T, U>>, U] // recursion
    : never;

/**
 * takes a 1:1 const-dictionary type, e.g. `{ [K in KeyType]: ValueType }` and produces
 * the reverse, i.e. `{ [K in ValueType]: KeyType }`
 * @example
 * ```ts
 * const dict = {
 *   a: 1,
 *   b: 2,
 * } as const
 *
 * // this will product an error unless it is laid out correctly:
 * const reverseDict: ReverseMap<typeof dict> = {
 *   "1": "a",
 *   "2": "b",
 * }
 * ```
 * @note with credits to https://stackoverflow.com/questions/60768302/typescript-reverse-map-key-value-type-to-value-key
 */
export type ReverseMap<T extends Record<keyof T, keyof any>> = {
  [K in keyof T as T[K]]: K;
};

/**
 * extracts the value of a Record-like type, like
 * @example
 * ```ts
 * const dict = {
 *   "a": "foo",
 *   "b": "bar",
 * } as const;
 *
 * // this is effectively equivalent to `"foo" | "bar"`
 * type DictValue = ValueOf<typeof dict>
 * ```
 * @note with credits to https://stackoverflow.com/questions/49285864/is-there-a-valueof-similar-to-keyof-in-typescript
 */
export type ValueOf<T> = T[keyof T];

/**
 * extracts the members of an `as const` array type into a union
 * @example
 * ```ts
 * const myArray = [
 *   "foo",
 *   "bar",
 *   "baz"
 * ] as const;
 *
 * type MyType = TypeFromArray<typeof myArray>;
 * // MyType is equivalent to `"foo" | "bar" | "baz"`
 * ```
 * @note this should fail on anything that is not defined `as const` or is not `readonly`
 */
export type TypeFromArray<T> =
  T extends ReadonlyArray<string> ? (string extends T[number] ? never : T[number]) : never;

/**
 * describes a dictionary given a type union, so that the keys are equivalent to its values
 * @example
 * ```ts
 * type MyType = "foo" | "bar" | "baz"
 * type MyMirror = Mirror<MyType>
 *
 * const myDict: MyMirror = {
 *   "foo": "foo",
 *   "bar": "bar",
 *   "baz": "baz",
 * }
 * ```
 */
export type Mirror<T extends string> = { [k in T]: k };

/**
 * type generic to turn a union into an intersection, e.g.
 * `A | B | C | ... | Z` => `A & B & C & ... Z`
 *
 * @example
 * ```ts
 * const Foo = () => { foo: string }
 * const Bar = () => { bar: number }
 *
 * // this is equivalent to { foo: string; bar: number }
 * type FooBar = UnionToIntersection<ReturnType<Foo | Bar>>;
 * ```
 * @note lifted from https://stackoverflow.com/questions/50374908/transform-union-type-to-intersection-type
 */
export type UnionToIntersection<U> = (U extends any ? (x: U) => void : never) extends (x: infer I) => void
  ? I
  : never;

/**
 * Recursively checks that all `name` fields in a readonly tuple are unique.
 * Returns `true` if all names are unique, or a descriptive string literal if a duplicate is found.
 */
export type UniqueNames<T, Seen extends string = never> = T extends readonly [infer Head, ...infer Tail]
  ? Head extends { readonly name: infer N extends string }
    ? N extends Seen
      ? `Duplicate feature name: ${N}`
      : UniqueNames<Tail, Seen | N>
    : UniqueNames<Tail, Seen>
  : true;

type BrowserNativeObject = Date | FileList | File;

/**
 * Recursively makes all properties optional at every level.
 *
 * Does not recurse into browser-native leaf types (`Date`, `File`, `FileList`).
 * Use for form state, message composition, and preview props.
 *
 * @example
 * ```ts
 * type Config = { editor: { theme: string; width: number } };
 * type PartialConfig = DeepPartial<Config>;
 * // => { editor?: { theme?: string; width?: number } }
 * ```
 */
export type DeepPartial<T> = T extends BrowserNativeObject ? T : { [K in keyof T]?: DeepPartial<T[K]> };

/**
 * Recursively makes properties optional, stopping recursion at non-object property types.
 * Semantics match Redux's `DeepPartial`. Use with {@link mergeDeep} and config merging.
 */
export type MergeDeepPartial<T> = {
  [K in keyof T]?: T[K] extends object ? MergeDeepPartial<T[K]> : T[K];
};

/**
 * Partial version of a nullable type, e.g.
 * `{ a: string | null }` => `{ a?: string }`
 */
export type PartialNullable<T> = { [K in keyof Partial<T>]: T[K] | null };

/**
 * Given a type, returns a type that describes an accessor function for the given type,
 * like:
 *
 * @example
 * ```ts
 * interface Foo { a: string; b: number }
 * type FooAccessor = AccessorFromType<Foo>
 *
 * const getValue: FooAccessor = (key) => ...
 * // this resolves to `string`
 * getValue("a")
 * // this resolves to `number`
 * getValue("b")
 * ```
 */
export type AccessorFromType<T extends object> = <Key extends keyof T>(key: Key) => T[Key];
